import { v4 as uuidv4 } from "uuid";
import stringify from "fast-safe-stringify";
import { BridgeResponse } from "@/types/bridge";
import { capturePageView } from "./loginContext";

type BridgeHandler = (...args: unknown[]) => unknown;
type BridgeParams = Record<string, unknown>;
type NativeBridgeChannel = {
  postMessage: (data: string) => void;
};

declare global {
  interface Window {
    quan: Record<string, BridgeHandler>;
    writeLog?: (data: Record<string, unknown>) => void;
  }
}

export class Bridge {
  private static instance: Bridge;
  private handlers: Map<string, BridgeHandler>;
  private timeout: number;

  private constructor() {
    this.handlers = new Map();
    this.timeout = 2000; // 默认超时时间
    this.initGlobalObjects();
  }

  private initGlobalObjects() {
    if (typeof window !== "undefined") {
      window.quan = window.quan || {};
    }
  }

  public static getInstance(): Bridge {
    if (!Bridge.instance) {
      Bridge.instance = new Bridge();
    }
    return Bridge.instance;
  }

  public setTimeout(timeout: number) {
    this.timeout = timeout;
  }

  public async call<T>(
    channel: string,
    method: string,
    params: BridgeParams = {}
  ): Promise<T> {
    const uuid = uuidv4();
    const payload = {
      ...this.sanitizeParams(params),
      method,
      uuid,
    };
    if (method === "sendTrackEvent") {
      capturePageView(payload);
    }
    const data = stringify(payload);

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        console.log("Bridge.call timeout", {
          method,
          uuid,
        });
        reject("Bridge call timeout"); //new Error会引起全局error rejec一个错误msg 使用时try catch就好
        // reject(new Error('Bridge call timeout'))
      }, this.timeout);

      window.quan[method] = (...args: unknown[]) => {
        const res = args[0] as BridgeResponse<T>;
        console.log("Bridge.call", {
          method,
          response: res,
        });
        clearTimeout(timeoutId);
        // if (res.uuid !== uuid) return

        this.log("Bridge.call", {
          method,
          response: res,
        });
        if (res.code === "0") {
          resolve(res.value);
        } else {
          reject(new Error(String(res.value)));
        }
      };

      try {
        const channelTarget = (
          window as unknown as Record<string, NativeBridgeChannel | undefined>
        )[channel];
        channelTarget?.postMessage(data);
      } catch (error) {
        clearTimeout(timeoutId);
        reject(error);
      }
    });
  }

  public register(name: string, callback: BridgeHandler) {
    this.handlers.set(name, callback);
    window.quan[name] = (...args: unknown[]) => {
      const result = callback(...args);
      const firstArg =
        args[0] && typeof args[0] === "object"
          ? (args[0] as { uuid?: string })
          : undefined;
      const response: BridgeResponse = {
        code: "0",
        value: result,
        uuid: firstArg?.uuid ?? "",
      };

      this.log("Bridge.register", {
        name,
        args,
        response,
      });

      return encodeURIComponent(stringify(response));
    };
  }

  private sanitizeParams(params: BridgeParams): Record<string, unknown> {
    const { element, ...rest } = params;
    return Object.entries(rest).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: Array.isArray(value)
          ? value.map((item) =>
              item && typeof item === "object"
                ? this.sanitizeParams(item as Record<string, unknown>)
                : item
            )
          : value && typeof value === "object"
            ? this.sanitizeParams(value as Record<string, unknown>)
            : value,
      }),
      {} as Record<string, unknown>
    );
  }

  private log(event: string, data: Record<string, unknown>) {
    window.writeLog?.({
      UM_Key_EventName: `UM_Event_${event}`,
      ...data,
    });
  }
}

// 创建单例实例
const bridge = Bridge.getInstance();

// 检查是否在 App 内
const isInApp = () => {
  if (typeof window === "undefined") return false;

  const ua = window.navigator.userAgent.toLowerCase();
  return ua.includes("seekee") || ua.includes("buscari");
};

// 只导出一次
export { bridge, isInApp };
